<template>
  <router-view
    v-if="project"
    v-slot="{ Component }"
  >
    <div class="h-full min-h-full">
      <component :is="Component" />
    </div>
  </router-view>
</template>

<script lang="ts" setup>
import {
  onBeforeMount, ref, Ref, provide, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Pages } from '@/router';
import { marketing } from '@/services/tracking';

import { getProjectState, ProjectState } from './api/get_project_state';

const project: Ref<ProjectState | undefined> = ref();
provide('project', project);

const route = useRoute();
const router = useRouter();
const projectId = ref(route.params.projectId as string);

async function initProject() {
  try {
    const projectState = await getProjectState(projectId.value);
    project.value = projectState;

    marketing.group(projectState.id, {
      name: projectState.name,
      createdAt: projectState.createdAt,
      currentUserRoleName: projectState.currentUserRoleName,
      onboardingStatus: projectState.states.onboardingState.status,
      ...projectState.features,
    });

    if (projectState.states.onboardingState.status !== 'approved') {
      const getRouteName = () => {
        if (!projectState.states.onboardingState.hasWatchedWelcomeVideo) {
          return Pages.welcomeVideo;
        }

        return Pages.onboarding;
      };

      router.replace({ name: getRouteName(), params: { projectId: projectId.value } });
    } else {
      const next = route.query.next as string;

      if (next) {
        router.replace({ path: next });
      }
    }
  } catch (error) {
    router.replace({ name: Pages.crash });
  }
}

onBeforeMount(async () => {
  initProject();
});

watch(() => route.params.projectId, async (newProjectId) => {
  if (newProjectId && newProjectId !== projectId.value) {
    projectId.value = newProjectId as string;
    await initProject();
  }
});

</script>
